import { useState, useEffect, useCallback } from 'react';
import { dataApi } from '@/lib/api';
import { Download, TrendingUp, TrendingDown, Minus, ChevronDown, Save } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const CHART_COLORS = ['#F66922', '#0A0A0A', '#737373', '#A3A3A3', '#D4D4D4', '#14B8A6', '#3B82F6'];
const TIMEFRAMES = [{ label: '30 Days', value: 30 }, { label: '60 Days', value: 60 }, { label: '90 Days', value: 90 }];

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}

function SkeletonCard({ h = 'h-64' }) {
  return <div className={`oaa-card skeleton-shimmer ${h} rounded-lg`} />;
}

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === 'down') return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-neutral-300" />;
}

export default function AudienceIntelligence() {
  const [verticals, setVerticals] = useState([]);
  const [vertical, setVertical] = useState('Financial Services');
  const [timeframe, setTimeframe] = useState(90);
  const [data, setData] = useState(null);
  const [topics, setTopics] = useState([]);
  const [content, setContent] = useState({ content: [], total: 0, page: 1, pages: 1 });
  const [brands, setBrands] = useState([]);
  const [audience, setAudience] = useState([]);
  const [insights, setInsights] = useState([]);
  const [segment, setSegment] = useState('JobFunction');
  const [loading, setLoading] = useState(true);
  const [contentPage, setContentPage] = useState(1);

  useEffect(() => {
    dataApi.getVerticals().then(r => setVerticals(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [aiRes, topicsRes, contentRes, brandsRes, audienceRes, insightsRes] = await Promise.all([
        dataApi.getAudienceIntelligence(vertical, timeframe),
        dataApi.getTopics(vertical, timeframe),
        dataApi.getContent(vertical, timeframe, 1),
        dataApi.getBrands(vertical, timeframe),
        dataApi.getAudience(vertical, timeframe, segment),
        dataApi.getInsights(vertical),
      ]);
      setData(aiRes.data);
      setTopics(topicsRes.data);
      setContent(contentRes.data);
      setBrands(brandsRes.data);
      setAudience(audienceRes.data);
      setInsights(insightsRes.data);
    } catch (e) {
      console.error('Failed to fetch data', e);
    } finally {
      setLoading(false);
    }
  }, [vertical, timeframe, segment]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchContentPage = async (page) => {
    setContentPage(page);
    try {
      const res = await dataApi.getContent(vertical, timeframe, page);
      setContent(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSegmentChange = async (seg) => {
    setSegment(seg);
    try {
      const res = await dataApi.getAudience(vertical, timeframe, seg);
      setAudience(res.data);
    } catch (e) { console.error(e); }
  };

  const maxTopic = topics.length ? topics[0]?.count || 1 : 1;
  const maxBrand = brands.length ? brands[0]?.count || 1 : 1;

  return (
    <div className="space-y-6" data-testid="audience-intelligence-page">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-[26px] font-bold text-[#0A0A0A] tracking-tight">Audience Intelligence Report for</h1>
          <select
            data-testid="vertical-selector"
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            className="h-9 px-3 pr-8 rounded-md border border-neutral-200 bg-white text-sm font-medium text-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] outline-none"
          >
            {verticals.map(v => (
              <option key={v.name} value={v.name}>{v.name}</option>
            ))}
          </select>
          <div className="flex bg-neutral-100 rounded-md overflow-hidden">
            {TIMEFRAMES.map(t => (
              <button
                key={t.value}
                data-testid={`timeframe-${t.value}`}
                onClick={() => setTimeframe(t.value)}
                className={`px-3 py-1.5 text-[11px] font-semibold tracking-wide uppercase transition-all ${
                  timeframe === t.value ? 'bg-[#0A0A0A] text-white' : 'text-neutral-500 hover:text-[#0A0A0A]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="save-report-btn" className="flex items-center gap-2 px-5 py-2 rounded-md bg-[#0A0A0A] text-white text-[11px] font-semibold tracking-wide uppercase hover:bg-[#262626] active:scale-[0.98]">
          <Save className="w-3.5 h-3.5" /> Save as new
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 gap-6">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : (
        <>
          {/* Three Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT - Accounts */}
            <div className="oaa-card p-6" data-testid="accounts-widget">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-[36px] font-bold text-[#0A0A0A] tabular-nums tracking-tight">{formatNum(data?.total_prospects)}</p>
                  <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mt-1">In-Market Prospects</p>
                </div>
                <button className="text-neutral-300 hover:text-neutral-500"><Download className="w-4 h-4" /></button>
              </div>
              {/* Buyer Stage Bar */}
              <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-neutral-400 mb-2">Buyer Stage</p>
              <div className="flex gap-1 mb-3 h-1.5 rounded-full overflow-hidden bg-neutral-100">
                <div className="bg-[#F66922] rounded-full" style={{ width: `${data?.buyer_stages?.early || 0}%` }} />
                <div className="bg-[#0A0A0A] rounded-full" style={{ width: `${data?.buyer_stages?.mid || 0}%` }} />
                <div className="bg-neutral-400 rounded-full" style={{ width: `${data?.buyer_stages?.late || 0}%` }} />
              </div>
              <div className="flex gap-4 text-xs text-neutral-500 mb-6">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F66922]" />Early {data?.buyer_stages?.early}%</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0A0A0A]" />Mid {data?.buyer_stages?.mid}%</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-neutral-400" />Late {data?.buyer_stages?.late}%</span>
              </div>
              {/* Stage Estimation */}
              <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-neutral-400 mb-2">Stage Estimation</p>
              <table className="w-full">
                <tbody>
                  {[
                    ['Awareness', data?.stage_estimation?.awareness],
                    ['Consideration', data?.stage_estimation?.consideration],
                    ['Decision', data?.stage_estimation?.decision],
                  ].map(([stage, count]) => (
                    <tr key={stage}>
                      <td className="py-1.5 text-sm text-neutral-600">{stage}</td>
                      <td className="py-1.5 text-sm font-semibold text-[#0A0A0A] text-right tabular-nums">{formatNum(count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MIDDLE - Signals Donut */}
            <div className="oaa-card p-5" data-testid="signals-widget">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400 mb-3">Engagements by Content Type</p>
              <div className="relative h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data?.content_types || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="type"
                    >
                      {(data?.content_types || []).map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      formatter={(v) => formatNum(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#0A0A0A] tabular-nums">{formatNum(data?.total_engagements)}</p>
                    <p className="text-[10px] text-neutral-400">Engagements</p>
                  </div>
                </div>
              </div>
              <table className="w-full mt-3">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-semibold text-neutral-300 uppercase pb-1">Content Type</th>
                    <th className="text-right text-[10px] font-semibold text-neutral-300 uppercase pb-1">Engagements</th>
                    <th className="text-right text-[10px] font-semibold text-neutral-300 uppercase pb-1 w-8">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.content_types || []).map((ct, i) => (
                    <tr key={ct.type}>
                      <td className="py-1 text-xs text-neutral-600 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {ct.type}
                      </td>
                      <td className="py-1 text-xs font-medium text-[#0A0A0A] text-right tabular-nums">{formatNum(ct.count)}</td>
                      <td className="py-1 text-right"><TrendIcon trend={ct.trend} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* RIGHT - Topics */}
            <div className="oaa-card p-6" data-testid="topics-widget">
              <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-neutral-400 mb-4">Topics</p>
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {topics.map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[12px] text-neutral-500 w-full max-w-[140px] truncate" title={t.name}>{t.name}</span>
                    <div className="flex-1 h-4 bg-neutral-100 rounded overflow-hidden">
                      <div
                        className="h-full rounded bg-[#F66922]"
                        style={{ width: `${(t.count / maxTopic) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-neutral-600 tabular-nums w-12 text-right">{formatNum(t.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Audience Need Insights */}
          <div className="oaa-card p-5" data-testid="insights-section">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-[#0A0A0A]">Audience Need Insights</h3>
              <span className="bg-[#F66922] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{insights.length}</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {insights.map((ins, i) => (
                <div key={i} className="insight-card bg-neutral-50 border border-neutral-200 rounded-lg p-4 flex flex-col">
                  <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{ins.title}</h4>
                  <p className="text-xs text-neutral-400 leading-relaxed mb-3 flex-1">{ins.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ins.personas.map((p, j) => (
                      <span key={j} className="bg-white border border-neutral-200 text-neutral-500 text-[10px] font-medium px-2 py-0.5 rounded-full">{p}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content + Brands */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 oaa-card p-5" data-testid="content-table">
              <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Content</h3>
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Content Title</th>
                    <th>Type</th>
                    <th className="text-right">Engagements</th>
                  </tr>
                </thead>
                <tbody>
                  {(content?.content || []).map((c, i) => (
                    <tr key={i}>
                      <td className="max-w-[300px] truncate" title={c.title}>{c.title}</td>
                      <td><span className="bg-neutral-100 text-neutral-500 text-[10px] font-medium px-2 py-0.5 rounded">{c.type}</span></td>
                      <td className="text-right font-medium tabular-nums">{formatNum(c.engagements)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {content?.pages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                  <span className="text-xs text-neutral-400">Page {content.page} of {content.pages}</span>
                  <div className="flex gap-1">
                    <button
                      disabled={content.page <= 1}
                      onClick={() => fetchContentPage(content.page - 1)}
                      className="px-3 py-1 text-xs rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"
                    >Prev</button>
                    <button
                      disabled={content.page >= content.pages}
                      onClick={() => fetchContentPage(content.page + 1)}
                      className="px-3 py-1 text-xs rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"
                    >Next</button>
                  </div>
                </div>
              )}
            </div>

            <div className="oaa-card p-5" data-testid="brands-panel">
              <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Brands</h3>
              <p className="text-xs text-neutral-400 mb-3">Competing brands researched by same audience</p>
              <div className="space-y-3">
                {brands.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-neutral-600 w-24 truncate">{b.name}</span>
                    <div className="flex-1 h-4 bg-neutral-100 rounded overflow-hidden">
                      <div className="h-full rounded bg-[#F66922]" style={{ width: `${(b.count / maxBrand) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-neutral-500 tabular-nums w-10 text-right">{formatNum(b.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Audience Cross-tab */}
          <div className="oaa-card p-5" data-testid="audience-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#0A0A0A]">Audience</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-400">Segment by:</span>
                {['JobFunction', 'JobLevel', 'EmpSize', 'RevSize'].map(s => (
                  <button
                    key={s}
                    data-testid={`segment-${s}`}
                    onClick={() => handleSegmentChange(s)}
                    className={`px-3 py-1 text-xs rounded-md font-medium transition-all ${
                      segment === s ? 'bg-[#F66922] text-white' : 'bg-white border border-neutral-200 text-neutral-500 hover:bg-neutral-50'
                    }`}
                  >
                    {s === 'JobFunction' ? 'Job Role' : s === 'JobLevel' ? 'Seniority' : s === 'EmpSize' ? 'Headcount' : 'Revenue'}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th className="text-right">NA</th>
                    <th className="text-right">EMEA</th>
                    <th className="text-right">APAC</th>
                    <th className="text-right">LATAM</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {audience.map((row, i) => {
                    const maxVal = Math.max(row.NA, row.EMEA, row.APAC, row.LATAM, 1);
                    const cellBg = (val) => {
                      const intensity = val / maxVal;
                      if (intensity > 0.7) return 'bg-emerald-100 text-emerald-800';
                      if (intensity > 0.3) return 'bg-emerald-50 text-emerald-700';
                      return '';
                    };
                    return (
                      <tr key={i}>
                        <td className="font-medium text-neutral-800 max-w-[200px] truncate">{row.name}</td>
                        <td className={`text-right tabular-nums ${cellBg(row.NA)}`}>{formatNum(row.NA)}</td>
                        <td className={`text-right tabular-nums ${cellBg(row.EMEA)}`}>{formatNum(row.EMEA)}</td>
                        <td className={`text-right tabular-nums ${cellBg(row.APAC)}`}>{formatNum(row.APAC)}</td>
                        <td className={`text-right tabular-nums ${cellBg(row.LATAM)}`}>{formatNum(row.LATAM)}</td>
                        <td className="text-right font-semibold tabular-nums">{formatNum(row.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
